<template>
    <div class="sp-paso">
        <header class="sp-paso__head">
            <span class="sp-icon-badge"><i class="pi pi-check-circle" aria-hidden="true"></i></span>
            <div>
                <h2 class="sp-h3">Confirma tu información</h2>
                <p>Revisa que todo esté correcto antes de crear tu cuenta.</p>
            </div>
        </header>

        <section v-for="bloque in bloques" :key="bloque.titulo" class="sp-resumen">
            <div class="sp-resumen__head">
                <h3>{{ bloque.titulo }}</h3>
                <button type="button" class="sp-enlace" @click="$emit('editar', bloque.paso)">
                    <i class="pi pi-pencil" aria-hidden="true"></i><span>Editar</span>
                </button>
            </div>
            <dl>
                <div v-for="fila in bloque.filas" :key="fila.etiqueta">
                    <dt>{{ fila.etiqueta }}</dt>
                    <dd>{{ fila.valor || '—' }}</dd>
                </div>
            </dl>
        </section>

        <div class="sp-legal-check">
            <p :class="{ 'is-ok': legal.acepto_terminos }">
                <i :class="legal.acepto_terminos ? 'pi pi-check-circle' : 'pi pi-circle'" aria-hidden="true"></i>
                <span>Términos y condiciones</span>
            </p>
            <p :class="{ 'is-ok': legal.acepto_privacidad }">
                <i :class="legal.acepto_privacidad ? 'pi pi-check-circle' : 'pi pi-circle'" aria-hidden="true"></i>
                <span>Política de privacidad</span>
            </p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PasoResumen',
    emits: ['editar'],
    props: {
        cuenta: { type: Object, required: true },
        negocio: { type: Object, required: true },
        facturacion: { type: Object, required: true },
        legal: { type: Object, required: true },
        categorias: { type: Array, default: () => [] },
        divipola: { type: Array, default: () => [] },
        rutNombre: { type: String, default: '' }
    },
    computed: {
        nombreCategoria() {
            const c = this.categorias.filter((x) => Number(x.id) === Number(this.negocio.id_categoria_negocio))[0];
            return c ? c.nombre : '';
        },
        ciudadNegocio() {
            return this.ciudadDe(this.negocio.id_divipola);
        },
        bloques() {
            return [
                {
                    titulo: 'Cuenta',
                    paso: 0,
                    filas: [
                        { etiqueta: 'Nombre', valor: this.cuenta.nombre },
                        { etiqueta: 'Correo', valor: this.cuenta.email },
                        { etiqueta: 'Celular', valor: this.cuenta.celular },
                        { etiqueta: 'Documento', valor: this.cuenta.documento }
                    ]
                },
                {
                    titulo: 'Negocio',
                    paso: 1,
                    filas: [
                        {
                            etiqueta: 'Tipo de persona',
                            valor: this.negocio.tipo_persona === 'JURIDICA' ? 'Persona jurídica' : 'Persona natural'
                        },
                        { etiqueta: 'Razón social', valor: this.negocio.razon_social },
                        { etiqueta: 'Nombre comercial', valor: this.negocio.nombre_comercial },
                        { etiqueta: 'Categoría', valor: this.nombreCategoria },
                        { etiqueta: 'Dirección', valor: this.negocio.direccion },
                        { etiqueta: 'Ciudad', valor: this.ciudadNegocio }
                    ]
                },
                {
                    titulo: 'Facturación',
                    paso: 2,
                    filas: [
                        {
                            etiqueta: 'Datos',
                            valor: this.facturacion.usa_datos_negocio
                                ? 'Los mismos del negocio'
                                : 'Datos diferentes'
                        },
                        { etiqueta: 'Correo de facturación', valor: this.facturacion.email_facturacion },
                        { etiqueta: 'RUT', valor: this.rutNombre ? this.rutNombre : 'Pendiente' }
                    ]
                }
            ];
        }
    },
    methods: {
        ciudadDe(idDivipola) {
            if (!idDivipola) return '';
            const fila = this.divipola.filter((f) => Number(f.id) === Number(idDivipola))[0];
            return fila ? fila.municipio + ', ' + fila.departamento : '';
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-paso {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.sp-paso__head {
    display: flex;
    align-items: center;
    gap: 1rem;

    p {
        margin-top: 0.25rem;
        color: var(--sp-text-soft);
        font-size: 0.92rem;
    }
}

.sp-resumen {
    padding: 1.1rem 1.25rem;
    background: var(--sp-surface-alt);
    border: 1px solid var(--sp-border);
    border-radius: 14px;
}

.sp-resumen__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;

    h3 {
        font-size: 0.85rem;
        font-weight: 800;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--sp-navy);
    }
}

.sp-resumen dl {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin: 0;
}

.sp-resumen div {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.sp-resumen dt {
    color: var(--sp-text-soft);
    font-size: 0.85rem;

    &::after {
        content: ':';
    }
}

.sp-resumen dd {
    margin: 0;
    color: var(--sp-navy);
    font-size: 0.85rem;
    font-weight: 600;
    overflow-wrap: anywhere;
}

.sp-enlace {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: none;
    border: 0;
    color: var(--sp-orange);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
}

.sp-legal-check {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;

    p {
        display: flex;
        align-items: center;
        gap: 0.45rem;
        color: var(--sp-text-soft);
        font-size: 0.88rem;
    }

    .is-ok {
        color: var(--sp-navy);
        font-weight: 600;

        i {
            color: #1f9d55;
        }
    }
}

@media (min-width: 640px) {
    .sp-resumen dl {
        grid-template-columns: 1fr 1fr;
    }
}
</style>
